/**
 * Auth-status badge cell with an optional submission-status icon.
 *
 * Renders the coloured auth-status pill (same as {@link BadgeWithICell}) and,
 * when a submission status is present, appends a small trailing icon: a spinner
 * for pending/in-progress states, a green tick for successful states, and a red
 * cross for errors. The submission-status icon is clickable and navigates to the
 * order's submission-status screen. Preserves the info-icon hover tooltip used
 * for Hold/Query auth statuses.
 *
 * @module BadgeWithStatusIconCell
 */
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SubmissionStatusEnum } from "../../../../data-model/submissionDocs";
import FileCheckIcon from "../../../../svg/file-check-icon";
import FilePenIcon from "../../../../svg/file-pen-icon";
import FileXIcon from "../../../../svg/file-x-icon";
import { InfoIcon } from "../../../../svg/info-icon";
import SpinnerIcon from "../../../../svg/spinner-icon";
import UserCheck16Icon from "../../../../svg/user-check-16-icon";
import ToolTipUsingPortal from "../../../toolTipUsingPortal/toolTipUsingPortal";
import { useTooltipData } from "./hooks/useTooltipData";

/** Props for {@link BadgeWithStatusIconCell}. */
interface BadgeWithStatusIconCellProps {
  value: {
    text: string;
    color?: string;
    bgColor?: string;
    displayText?: string;
    showInfoIcon?: boolean;
    fetchFn?: () => Promise<string>;
    submissionStatus?: SubmissionStatusEnum;
    orderId?: string;
  };
  [key: string]: any;
}

/**
 * Maps a submission status to its trailing icon.
 *
 * @param status - The submission status driving the icon (optional).
 * @returns The icon element, or null when no icon should be shown.
 */
const renderSubmissionStatusIcon = (
  status?: SubmissionStatusEnum,
): JSX.Element | null => {
  switch (status) {
    case "in_progress":
    case "fax_submitted":
    case "queued":
      return <SpinnerIcon height="12" width="12" />;
    case "completed":
      return <FileCheckIcon height="16" width="16" />;
    case "submission_completed":
      return <UserCheck16Icon height="16" width="16" />;
    case "fax_delivered":
      return <FileCheckIcon height="16" width="16" />;
    case "error":
    case "fax_error":
    case "fax_failed":
      return <FileXIcon height="16" width="16" />;
    case "saved_draft":
      return <FilePenIcon height="16" width="16" />;
    default:
      return null;
  }
};

/**
 * Renders the auth-status badge alongside an optional submission-status icon.
 *
 * @input props - The cell value (badge text/colours, tooltip data, submission status).
 * @returns The badge cell element.
 */
export const BadgeWithStatusIconCell: React.FC<
  BadgeWithStatusIconCellProps
> = ({ value }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { tooltipContent } = useTooltipData({
    showTooltip,
    fetchFn: value?.fetchFn,
    displayText: value?.displayText,
  });

  const statusIcon = renderSubmissionStatusIcon(value?.submissionStatus);

  /**
   * Opens the submission-status screen for the order without triggering the
   * surrounding row-click navigation.
   *
   * @param event - The originating mouse click on the status icon.
   */
  const handleStatusIconClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    event.stopPropagation();
    if (value?.orderId) {
      navigate(`/worklists/submission-status/${value.orderId}`);
    }
  };

  return (
    <div
      className="flex w-full flex-row items-center gap-2"
      onMouseEnter={() => setShowTooltip(value?.showInfoIcon ?? false)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div
        className="table-cell-badge"
        style={{
          color: value.color || "#000000",
          backgroundColor: value.bgColor || "#F5F5F5",
        }}
      >
        {value.text}
      </div>
      {statusIcon && (
        <button
          type="button"
          title="View submission status"
          aria-label="View submission status"
          onClick={handleStatusIconClick}
          className="flex h-3.5 w-3.5 shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0"
        >
          {statusIcon}
        </button>
      )}
      <div ref={parentRef}>{value.showInfoIcon && <InfoIcon />}</div>
      {showTooltip && (
        <ToolTipUsingPortal
          placement="left"
          showTooltip={showTooltip}
          parentRef={parentRef}
          children={<div>{tooltipContent}</div>}
          wrapText
        />
      )}
    </div>
  );
};

export default BadgeWithStatusIconCell;
